import './FinanciarWizard.scss';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { BackButton, RecalcularButton } from '../../components/buttons/Buttons';
import SimulationCard from '../../components/cards/SimulationCard';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import DollarIcon from '../../components/dollaricon/DollarIcon';
import { ReactComponent as CalendarIcon } from '../../img/calendar-days.svg';
import { ReactComponent as CarIcon } from '../../img/car-black.svg';
import { ReactComponent as CoinsIcon } from '../../img/coins-stacked.svg';
import { ReactComponent as PercentageIcon } from '../../img/percentage.svg';
import { ReactComponent as PieChartIcon } from '../../img/pie-chart.svg';
import { simulateCredit } from '../../services/apiService';
import { cleanCurrencyValue, footAmoutMessage, validarPie } from '../../utils/formUtils';
import useTrackEvent from '../../utils/trackEvent';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { useTenant } from '../../context/TenantContext';

const CUOTAS_VALUES = [6, 12, 18, 24, 30, 36, 42, 48, 60];

// Componente memoizado para el popup
const Popup = memo(({ creditType, onClose }) => {
  return (
    <div className="popup">
      <div className="popup-header">
        <h1>
          <strong>{creditType === 'CF' ? 'CRÉDITO CUOTA FIJA' : 'CRÉDITO INTELIGENTE'}</strong>
        </h1>
        <p onClick={onClose}>
          <strong className="custom-strong">Cerrar X</strong>
        </p>
      </div>
      <h4>
        <strong className="custom-strong">Características:</strong>
      </h4>
      <div className="popup-item">
        <CalendarIcon className="calendar-icon" />
        <p>
          <strong className="custom-strong">Plazo:</strong>{' '}
          {creditType === 'CF' ? '6 a 60 meses*' : '24 + Cuotón / 36 + Cuotón / 48 + Cuotón*'}
        </p>
      </div>
      {creditType === 'CI' && (
        <div className="popup-item">
          <DollarIcon />
          <p>
            <strong className="custom-strong">Cuotón:</strong> Cuota final alta que permite pagar
            menos mensualmente durante el crédito.
          </p>
        </div>
      )}
      <div className="popup-item">
        <PercentageIcon className="percentage-icon" />
        <p>
          <strong className="custom-strong">Tasa:</strong> Fija mensual
        </p>
      </div>
      <div className="popup-item">
        <PieChartIcon className="piechart-icon" />
        <p>
          <strong className="custom-strong">Pie:</strong>{' '}
          {creditType === 'CF' ? 'Mínimo 20%*' : 'Mínimo 20% | Máximo 30%'}
        </p>
      </div>
      <div className="popup-item">
        <CoinsIcon className="coins-icon" />
        <p>
          <strong className="custom-strong">Financiamiento:</strong>{' '}
          {creditType === 'CF'
            ? 'Autos con 7 años de antigüedad máxima'
            : 'Autos con 2 años de antigüedad máxima'}
        </p>
      </div>
      <div className="popup-item">
        <CarIcon className="car-icon" />
        <p>
          <strong className="custom-strong">Cuotas:</strong>{' '}
          {creditType === 'CF'
            ? 'Iguales durante todo el periodo'
            : 'Al final del periodo renueva tu auto o refinancia el cuotón*'}
        </p>
      </div>
      <div className="extra-info">
        <ul>
          <li>
            La simulación realizada tiene una validez de 30 días, valores cuota pueden variar al
            realizar el cálculo de la contratación del crédito automotriz definitivo.
          </li>
          <li>Cuota incluye seguro de desgravamen y cesantía.</li>
          <li>
            Cuota simulada aplica única y exclusivamente a la contratación de créditos para
            vehículos particulares.
          </li>
          <li>
            <strong className="custom-strong">Crediautos</strong> se reserva el derecho de
            aprobación en función de las políticas crediticias vigentes y previa comprobación de
            antecedentes financieros y comerciales del solicitante.
          </li>
          <li>
            Para mayor información sobre nuestros requisitos, revisa&nbsp;
            <a href="/" target="_blank" rel="noopener noreferrer" style={{ color: '#16D7DB' }}>
              Condiciones para Acceder a Créditos
            </a>
            .
          </li>
        </ul>
      </div>
    </div>
  );
});

// Componente memoizado para el layout mobile
const MobileLayoutSimulation = memo(
  ({
    existingData,
    onInputChange,
    onCalculate,
    selectedYear,
    onYearChange,
    isLoading,
    getYearRange,
  }) => {
    return (
      <div
        className="simulacion-cuotas-movil"
        style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
      >
        {/* Valor del vehículo */}
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Valor aproximado del vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              name="carValue"
              className="form-input"
              style={{ width: '100%' }}
              type="text"
              value={existingData.carValue}
              onChange={onInputChange}
            />
          </div>
        </div>

        {/* Monto del pie */}
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Monto del pie *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              name="footAmount"
              className="form-input"
              style={{ width: '100%' }}
              type="text"
              value={existingData.footAmount}
              onChange={onInputChange}
            />
          </div>
        </div>

        {/* Número de cuotas */}
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Número de cuotas *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="fee"
              className="form-input-column cuotas"
              value={existingData.fee || ''}
              style={{ width: '100%' }}
              onChange={onInputChange}
            >
              {CUOTAS_VALUES.map((cuota) => (
                <option key={cuota} value={cuota}>
                  {cuota}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Año del vehículo */}
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Año del vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="caryear"
              required
              className="form-input-column"
              style={{ width: '100%' }}
              onChange={(e) => onYearChange(e.target.value)}
              value={selectedYear}
            >
              <option value="" disabled hidden>
                Seleccionar año
              </option>
              {getYearRange().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botón recalcular */}
        <div className="formControl-root">
          <RecalcularButton onClick={onCalculate} isLoading={isLoading} />
        </div>
      </div>
    );
  }
);

const DesktopLayoutSimulation = memo(
  ({
    existingData,
    onInputChange,
    onCalculate,
    selectedYear,
    onYearChange,
    isLoading,
    getYearRange,
  }) => {
    return (
      <div className="simulacion-cuotas">
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Valor aproximado del vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              name="carValue"
              className="form-input"
              type="text"
              value={existingData.carValue}
              onChange={onInputChange}
            />
          </div>
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Monto del pie *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              name="footAmount"
              className="form-input"
              type="text"
              value={existingData.footAmount}
              onChange={onInputChange}
            />
          </div>
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Número de cuotas *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="fee"
              className="form-input-column cuotas"
              onChange={onInputChange}
              value={existingData.fee || ''}
            >
              {CUOTAS_VALUES.map((cuota) => (
                <option key={cuota} value={cuota}>
                  {cuota}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Año del vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="caryear"
              required
              className="form-input-column"
              onChange={(e) => onYearChange(e.target.value)}
              value={selectedYear}
            >
              <option value="" disabled hidden>
                Seleccionar año
              </option>
              {getYearRange().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        <RecalcularButton onClick={onCalculate} isLoading={isLoading} />
      </div>
    );
  }
);

const Simulacion = ({ onNextStep, data, onPreviousStep }) => {
  const hasPushedPageView = useRef(false);
  const { isMeliTenant } = useTenant();
  const { flags, isLoadingFlags } = useFeatureFlags();
  const { pushPageView, pushCustomEvent } = useTrackEvent();
  const [state, setState] = useState(() => ({
    existingData: JSON.parse(sessionStorage.getItem('formData')) || {},
    simResults: data?.simulationData?.creditSimulation || [],
    showOptions: true,
    showPopup: false,
    creditType: '',
    error: null,
    isLoading: false,
    selectedYear: JSON.parse(sessionStorage.getItem('formData'))?.caryear || '',
  }));
  const executiveRut = !isLoadingFlags ? flags.executiveRut : undefined;
  const sellerRut = !isLoadingFlags ? flags.sellerRut : undefined;
  const dealerRut = !isLoadingFlags ? flags.dealerRut : undefined;
  const storeCode = !isLoadingFlags ? flags.storeCode : undefined;
  const storeCodeMeli = !isLoadingFlags ? flags.storeCodeMeli : undefined;

  useEffect(() => {
    sessionStorage.setItem('formData', JSON.stringify(state.existingData));
  }, [state.existingData]);

  useEffect(() => {
    if (!hasPushedPageView.current) {
      pushPageView(
        'vpv',
        'Crédito Automotríz',
        '3',
        '/atencion-normal/simulacion',
        'Resultado Simulacion - Atención Normal'
      );

      hasPushedPageView.current = true;
    }
  }, []);

  const getYearRange = useCallback(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
    const startYear = endYear - 7;

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);
  }, []);

  const handlePopupClick = useCallback((type) => {
    setState((prev) => ({
      ...prev,
      creditType: type,
      showPopup: !prev.showPopup,
    }));
  }, []);

  const handleClosePopup = useCallback(() => {
    setState((prev) => ({ ...prev, showPopup: false }));
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    let rawValue = value;
    let maxLength = 15;

    if (name === 'carValue') {
      rawValue = cleanCurrencyValue(value).toString();
      maxLength = 9;
    } else if (name === 'footAmount') {
      rawValue = cleanCurrencyValue(value).toString();
      maxLength = 8;
    }

    const limitedValue = rawValue.slice(0, maxLength);

    setState((prev) => ({
      ...prev,
      existingData: { ...prev.existingData, [name]: limitedValue },
    }));
  }, []);

  const handleYearChange = useCallback((year) => {
    setState((prev) => ({
      ...prev,
      selectedYear: year,
      existingData: { ...prev.existingData, caryear: year },
    }));
  }, []);

  const handleCalculate = useCallback(async () => {
    const eventData = {
      flujo: 'Crédito Automotríz',
      button_step_value: '3',
      step: '3',
      page: '/atencion-normal/simulacion',
      PageName: 'Resultado Simulacion - Atención Normal',
      button_step_name: 'recalcular',
      version: 'v2',
    };

    pushCustomEvent('event_buttons', eventData);

    const existingData = state.existingData;
    const selectedYear = state.selectedYear;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    const carValue = parseFloat(String(existingData.carValue).replace(/\D/g, ''));
    const footAmount = parseFloat(String(existingData.footAmount).replace(/\D/g, ''));
    const { fee } = existingData;

    if (!validarPie(footAmount, carValue, fee, selectedYear)) {
      setState((prev) => ({
        ...prev,
        error: footAmoutMessage(carValue, selectedYear, fee),
        isLoading: false,
      }));

      return;
    }

    try {
      const response = await simulateCredit({
        ...existingData,
        sourceName: isMeliTenant ? 'MercadoLibre' : 'Crediautos',
        channelRequest: isMeliTenant ? 'Mercadolibre' : `${existingData.typeFinancing}_v2`,
        storeCode: isMeliTenant ? storeCodeMeli : storeCode,
        executiveRut,
        sellerRut,
        dealerRut,
      });
      const newSimResults = response?.simulationData?.creditSimulation || [];

      setState((prev) => ({
        ...prev,
        simResults: newSimResults,
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: '¡Ups! Algo salió mal. Intenta nuevamente en unos segundos.',
        isLoading: false,
      }));
    }
  }, [pushCustomEvent, state.existingData, state.selectedYear]);

  return (
    <div className="financiar-wizard-simulacion">
      <div className="financiar-wizard-simulacion-header">
        <h1>Resultados de simulación</h1>
        <p>Te ofrecemos distintas alternativas según el monto solicitado</p>
      </div>

      <div className="financiar-wizard-simulacion-content">
        {isMobile ? (
          <MobileLayoutSimulation
            existingData={state.existingData}
            onInputChange={handleInputChange}
            onCalculate={handleCalculate}
            selectedYear={state.selectedYear}
            onYearChange={handleYearChange}
            isLoading={state.isLoading}
            getYearRange={getYearRange}
          />
        ) : (
          <DesktopLayoutSimulation
            existingData={state.existingData}
            onInputChange={handleInputChange}
            onCalculate={handleCalculate}
            selectedYear={state.selectedYear}
            onYearChange={handleYearChange}
            isLoading={state.isLoading}
            getYearRange={getYearRange}
          />
        )}

        {state.error && <div className="error-msg">{state.error}</div>}

        {state.showOptions && (
          <div className="options-list">
            {state.showPopup && <Popup creditType={state.creditType} onClose={handleClosePopup} />}

            {state.simResults.length > 0 ? (
              state.simResults.map((option, index) => (
                <SimulationCard
                  key={`${option.type}-${index}`}
                  option={option}
                  simulationId={data?.simulationData?.id}
                  handlePopupClick={handlePopupClick}
                />
              ))
            ) : (
              <p>No options available</p>
            )}

            <div className="simulacion-formalizar-info">
              <p>
                Luego de confirmar la simulación, uno de nuestros ejecutivos se pondrá en contacto
                contigo para atender tu requerimiento.
              </p>
            </div>
          </div>
        )}

        <div className="nav-button-container">
          <BackButton onClick={onPreviousStep} />
        </div>
      </div>
    </div>
  );
};

export default memo(Simulacion);
